import { z } from 'zod';
import { I18nStringSchema, ShortIdSchema, TimestampSchema } from './primitives.js';
import { ChallengeRewardSchema } from './challenge.js';

/**
 * Spin Wheel — an operator-configured prize game. The operator authors a wheel
 * of prize SEGMENTS, each carrying a reward + a weight (odds). A customer earns
 * one spin per paid wash (banked, capped). Spinning calls the `spinTheWheel`
 * callable, which picks the winning segment with a SECURE server-side weighted
 * RNG (`crypto.randomInt`), grants the reward, and returns the winning INDEX —
 * the client only animates the wheel to that slice and can never influence or
 * predict the outcome. All prize logic is 100% server-side (see
 * `packages/shared/src/loyalty/spin-wheel.ts` for the pure selection maths).
 *
 * Config lives INLINE on the tenant loyalty config
 * (`tenants/{tid}/loyaltyProgram/config`.spinWheel), NOT in a subcollection —
 * a wheel is a small fixed prize table, edited as one unit (parallel to how the
 * `punchCard` + `referrals` config objects live on the same doc). Per-customer
 * spin RESULTS are recorded at `users/{uid}/loyaltyAccounts/{tid}/spins/{spinId}`.
 */

/**
 * One prize slice. `weight` is a relative-odds integer: a segment's win chance
 * is `weight / sum(all weights)`. Operators are encouraged to include a "no win"
 * segment (a `reward: { kind: 'none' }` slice) so the wheel isn't all-winners.
 * Reuses the achievement/challenge/referral reward union so the grant path is
 * identical to every other loyalty reward (points / voucher / none).
 */
export const SpinSegmentSchema = z
  .object({
    id: ShortIdSchema.describe(
      'Short segment id; stable across edits so odds/history stay attributable',
    ),
    label: I18nStringSchema.describe('Operator-authored slice label (per-locale inline object)'),
    reward: ChallengeRewardSchema.describe(
      'What this slice grants when it wins — points / voucher / none (a "no win" slice uses none)',
    ),
    weight: z
      .number()
      .int()
      .min(1)
      .max(1000)
      .describe(
        'Relative odds weight (>= 1). Win chance = this weight / sum of all segment weights',
      ),
  })
  .describe('One prize slice on the wheel');
export type SpinSegment = z.infer<typeof SpinSegmentSchema>;

/**
 * The wheel config object embedded on the loyalty config doc. Ships EMPTY
 * (`segments: []`) so a fresh tenant is inert until the operator authors slices;
 * a wheel is only PLAYABLE once it has >= 2 segments AND the feature flag is on
 * (see `isSpinWheelPlayable`). `.default(...)` keeps the field backward-compatible
 * — pre-spin-wheel configs parse unchanged and pick up the empty default.
 */
export const SpinWheelConfigSchema = z
  .object({
    segments: z
      .array(SpinSegmentSchema)
      .default([])
      .describe(
        'Prize slices; a playable wheel needs at least 2. Empty = operator has not authored yet',
      ),
    maxBankedSpins: z
      .number()
      .int()
      .min(2)
      .max(20)
      .default(5)
      .describe(
        'Cap on unspent spins a customer can bank (1 accrues per paid wash, up to this cap)',
      ),
  })
  .default({ segments: [], maxBankedSpins: 5 })
  .describe('Spin Wheel prize game config (server-side weighted RNG; anti-cheat)');
export type SpinWheelConfig = z.infer<typeof SpinWheelConfigSchema>;

/**
 * A recorded spin RESULT. Lives at
 * `users/{uid}/loyaltyAccounts/{tenantId}/spins/{spinId}` — doc id = the freshly
 * minted spinId, so the write is naturally idempotent and the deterministic
 * reward grant ids (`spin_${spinId}` / `sp_${spinId}`) can never double-pay.
 * Server-only write (admin SDK via `spinTheWheel`); the owning customer reads
 * their own via the per-uid self-read rule (parallel to the points ledger +
 * earned achievements + challenge progress).
 *
 * `reward` is a SNAPSHOT of what the winning segment granted at spin time, so a
 * later edit to the segment's reward never rewrites history.
 */
export const SpinResultSchema = z
  .object({
    id: ShortIdSchema.describe('Spin id; equals the Firestore doc id'),
    segmentId: ShortIdSchema.describe('Id of the winning segment'),
    segmentIndex: z
      .number()
      .int()
      .nonnegative()
      .describe('Index of the winning segment in the wheel — the client animates to this slice'),
    reward: ChallengeRewardSchema.describe('Snapshot of the reward the winning segment granted'),
    at: TimestampSchema.describe('When the spin was resolved server-side'),
  })
  .describe('One recorded spin outcome for a customer at a tenant');
export type SpinResult = z.infer<typeof SpinResultSchema>;
